import { useEffect, useRef } from "react";

/**
 * Iter-156E — calm cross-device refresh.
 *
 * Re-runs `callback` whenever the tab regains focus (`focus` event)
 * or becomes visible again (`visibilitychange` event). This is the
 * standard React Query / SWR pattern — no websockets, no aggressive
 * polling, just "if the user is looking at this surface, make sure
 * what they see is current".
 *
 * Use cases on OnSetFlow:
 * - Script visibility toggled by Owner → Contributor's open ScriptPage
 *   re-fetches the project so the lock banner appears naturally on
 *   their next focus.
 * - Note added by user A → user B's SupervisorNotesList reloads on
 *   focus.
 *
 * Cooldown: throttled to one call per 1.5s so a flurry of focus events
 * (e.g. fast tab swapping) doesn't hammer the API. Override via
 * `cooldownMs` if you need lower latency.
 */
export function useRevalidateOnFocus(callback, { enabled = true, cooldownMs = 1500 } = {}) {
    const cbRef = useRef(callback);
    cbRef.current = callback;
    const lastRunRef = useRef(0);

    useEffect(() => {
        if (!enabled) return undefined;
        const run = () => {
            const now = Date.now();
            if (now - lastRunRef.current < cooldownMs) return;
            lastRunRef.current = now;
            try {
                const r = cbRef.current?.();
                // Swallow rejections so an isolated failure (e.g. a
                // 403 mid-transition) never blows up the focus handler.
                if (r && typeof r.catch === "function") {
                    r.catch(() => {});
                }
            } catch {
                /* ignore */
            }
        };
        const onVisibility = () => {
            if (document.visibilityState === "visible") run();
        };
        window.addEventListener("focus", run);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("focus", run);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [enabled, cooldownMs]);
}
